import { ReactNode, useState, useId, InputHTMLAttributes } from "react";
import { Box, styled } from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { tokens } from "@styles/tokens";

interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  leadingIcon?: ReactNode;
  error?: string;
}

/**
 * Design-system text field: label, optional leading icon, built-in password
 * reveal, and a focus ring + error state in brand/semantic tokens. The only
 * input features should use.
 */
export const Field = ({
  label,
  leadingIcon,
  error,
  type = "text",
  id,
  ...input
}: FieldProps) => {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword && reveal ? "text" : type;

  return (
    <Wrap>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Control $error={!!error}>
        {leadingIcon && <Leading>{leadingIcon}</Leading>}
        <StyledInput
          id={inputId}
          type={resolvedType}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...input}
        />
        {isPassword && (
          <Trailing
            type="button"
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Hide password" : "Show password"}
          >
            {reveal ? <EyeOff size={18} /> : <Eye size={18} />}
          </Trailing>
        )}
      </Control>
      {error && <ErrorText id={errorId}>{error}</ErrorText>}
    </Wrap>
  );
};

const Wrap = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(1.5),
});

const Label = styled("label")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.body,
}));

const Control = styled(Box, {
  shouldForwardProp: (p) => p !== "$error",
})<{ $error: boolean }>(({ theme, $error }) => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(2),
  height: 48,
  padding: `0 ${tokens.space(3.5)}px`,
  borderRadius: tokens.radius.md,
  backgroundColor: theme.skuey.surface,
  border: `1px solid ${$error ? theme.skuey.danger : theme.skuey.border}`,
  transition: "border-color .15s ease, box-shadow .15s ease",
  "&:focus-within": {
    borderColor: $error ? theme.skuey.danger : theme.skuey.brand[600],
    boxShadow: `0 0 0 3px ${
      $error ? "rgba(220,38,38,0.12)" : "rgba(37,99,235,0.12)"
    }`,
  },
}));

const Leading = styled(Box)(({ theme }) => ({
  display: "flex",
  color: theme.skuey.subtle,
  flexShrink: 0,
}));

const StyledInput = styled("input")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  padding: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  color: theme.skuey.ink,
  "&::placeholder": { color: theme.skuey.subtle, opacity: 1 },
}));

const Trailing = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  color: theme.skuey.subtle,
  flexShrink: 0,
  "&:hover": { color: theme.skuey.body },
}));

const ErrorText = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.tiny[0],
  fontWeight: tokens.font.weight.medium,
  color: theme.skuey.danger,
}));
